"use client"

import React, { useState } from "react"
import { useRouter } from "next/navigation"
import { toast } from "sonner"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Key, Mail, Phone, User, Contact } from "lucide-react"

import { HeadingText } from "@/components/atoms/HeadingText/heading-text"
import { SubmitButton } from "@/components/molecules/SubmitButton/submit-button"
import Input from "@/components/atoms/Input/input"
import PhoneInput from "@/components/molecules/PhoneInput"
import { Checkbox } from "@/components/atoms/Checkbox/checkbox"
import { Label } from "@/components/atoms/Label/label"
import useLocaleStore from "@/app/stores/useLocaleStore"
import { useSession } from "@/contexts/SessionContext"
import {
  forgotPasswordAction,
  loginUserAction,
  registerUserAction,
  resetPasswordAction
} from "@/actions/user/auth.action"
import { authSchemas } from "@/app/schema/auth"

type AuthMode = "signup" | "login" | "forgot-password" | "reset-password"

type FormValues = {
  name: string
  email: string
  phoneNumber: string
  contact: string
  password: string
  confirmPassword: string
  terms: boolean
  token: string
}

type AuthFormProps = {
  mode: AuthMode
  heading: string
  buttonText: string
  secondaryLinkLabel: string
  secondaryLinkText: string
  secondaryLinkHref: string
  callbackUrl?: string
}

const schemaMap = {
  signup: authSchemas.register,
  login: authSchemas.login,
  "forgot-password": authSchemas.forgotPassword,
  "reset-password": authSchemas.resetPassword,
}

type FormFieldProps<T extends keyof FormValues> = {
  name: T
  id: string
  placeholder: string
  icon?: React.ReactNode
  type?: string
  control?: any
  register?: any
  errors?: any
}

const FormField = <T extends keyof FormValues>({
  name,
  id,
  placeholder,
  icon,
  type = "text",
  control,
  register,
  errors
}: FormFieldProps<T>) => {
  const hasError = !!errors?.[name]

  return (
    <div className="mb-4">
      <div
        className={`relative border rounded-lg transition-colors duration-150 ease-in-out
          ${hasError
            ? "border-red-500 dark:border-red-400"
            : "border-gray-300 dark:border-gray-600"}
          focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200
          dark:focus-within:border-blue-500 dark:focus-within:ring-blue-900`}
      >
        {name === "phoneNumber" && control ? (
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <PhoneInput
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                placeholder={placeholder}
              />
            )}
          />
        ) : (
          <Input
            {...(register && register(name))}
            className="w-full px-4 py-3 bg-transparent border-none focus:ring-0 focus:outline-none"
            type={type}
            name={name}
            id={id}
            placeholder={placeholder}
            aria-label={placeholder}
            aria-describedby={`Error${id}`}
            icon={icon as React.ReactElement}
            iconClassName={`${hasError
              ? 'text-red-500 dark:text-red-400'
              : 'text-gray-400 dark:text-gray-500'} 
              group-focus-within:text-blue-500 dark:group-focus-within:text-blue-400`}
          />
        )}
      </div>
      <p className="min-h-[18px] text-red-500 mt-1 text-sm">{errors?.[name]?.message || ""}</p>
    </div>
  )
}

const AuthForm: React.FC<AuthFormProps> = ({
  mode,
  heading,
  buttonText,
  secondaryLinkText,
  secondaryLinkLabel,
  secondaryLinkHref,
  callbackUrl
}) => {
  const router = useRouter()
  const locale = useLocaleStore((state) => state.locale)
  const { refreshSession } = useSession()
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    mode: "onBlur",
    resolver: zodResolver(schemaMap[mode]),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      contact: "",
      password: "",
      confirmPassword: "",
      terms: false,
      token: ""
    }
  })

  const acceptedTerms = watch("terms") || false
  const token = watch("token")

  const actionMap: Record<AuthMode, (formData: FormData) => Promise<any>> = {
    signup: async (formData) => registerUserAction(null, formData),
    login: async (formData) => loginUserAction(null, formData),
    "forgot-password": async (formData) => forgotPasswordAction(null, formData),
    "reset-password": async (formData) => resetPasswordAction(null, formData)
  }

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)

    try {
      const formData = new FormData()

      if (mode === "reset-password") {
        formData.append("token", token)
        formData.append("password", data.password)
        formData.append("confirmPassword", data.confirmPassword)
      } else {
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, String(value))
          }
        })
      }

      if (callbackUrl) {
        formData.append('callbackUrl', callbackUrl)
      }

      const response = await actionMap[mode](formData)

      if (response.success) {
        toast.success(response.message)
        
        // Refresh session before navigation for login
        if (mode === "login") {
          await refreshSession()
        }

        if (response.redirectTo) {
          router.push(response.redirectTo)
        }
        return
      }

      // Only show toast on errors
      if (response.message) {
        toast.error(
          <div className="space-y-2">
            <p>{response.message || 'Something went wrong'}</p>
            <p className="text-xs text-gray-500">
              Need help?{' '}
              <a href="/about#contact-form" className="text-blue-500 hover:underline">
                Contact Support
              </a>
            </p>
          </div>
        );
      }
    } catch (err: any) {
      toast.error(
        <div className="space-y-2">
          <p>{err?.message || 'Something went wrong'}</p>
          <p className="text-xs text-gray-500">
            Need help?{' '}
            <a href="/about#contact-form" className="text-blue-500 hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      );
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-md min-w-[300px] min-h-[50vh] flex flex-col items-center justify-center px-4 sm:px-6">
      <div className="w-full bg-white p-8 rounded-2xl shadow-xl border border-gray-100
                      dark:bg-gray-900 dark:border-gray-800">
        <div className="flex flex-col justify-center text-center">
          <HeadingText level={3} className="pb-2 text-center text-2xl font-bold text-gray-900 dark:text-white">
            {heading}
          </HeadingText>
          <hr className="my-5 w-full border-t border-gray-200 dark:border-gray-700" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {callbackUrl && <input type="hidden" name="callbackUrl" value={callbackUrl} />}

          {mode === "signup" && (
            <>
              <FormField name="name" id="name" placeholder={locale === "en" ? "Full Name" : "ሙሉ ስም"} icon={<User />} register={register} errors={errors} />
              <FormField name="email" id="email" placeholder={locale === "en" ? "E-Mail" : "ኢሜል"} icon={<Mail />} register={register} errors={errors} />
              <FormField name="phoneNumber" id="phoneNumber" placeholder="+1 249-345-6078" icon={<Phone />} control={control} errors={errors} />
            </>
          )}

          {(mode === "login" || mode === "forgot-password") && (
            <FormField name="contact" id="contact" placeholder={locale === "en" ? "Phone or Email" : "ስልክ ወይም አሜል"} icon={<Contact />} register={register} errors={errors} />
          )}

          {mode === "reset-password" && (
            <>
              <div className={`flex justify-center mb-6 ${token?.length === 6 ? "hidden" : ""}`}>
                <Controller
                  name="token"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength={6}
                      className="w-full text-center text-3xl tracking-[1em] font-semibold border-b-2 border-gray-300 focus:border-blue-500 focus:outline-none bg-transparent py-2 dark:border-gray-600 dark:text-white"
                      {...field}
                    />
                  )}
                />
              </div>
              <p className="min-h-[18px] text-red-500 mt-1 text-sm text-center">{errors.token?.message || ""}</p>

              <div className={`${token?.length === 6 ? "block" : "hidden"} space-y-4`}>
                <FormField type="password" name="password" id="password" placeholder={locale === "en" ? "New Password" : "አዲስ የይለፍ ቃል"} icon={<Key />} register={register} errors={errors} />
                <FormField type="password" name="confirmPassword" id="confirmPassword" placeholder={locale === "en" ? "Confirm Password" : "አዲሱን የይለፍ ቃል አረጋግጥ"} icon={<Key />} register={register} errors={errors} />
              </div>
            </>
          )}

          {(mode === "signup" || mode === "login") && (
            <FormField type="password" name="password" id="password" placeholder={locale === "en" ? "Password" : "የይለፍ ቃል"} icon={<Key />} register={register} errors={errors} />
          )}

          {mode === "signup" && (
            <div className="mb-4 mt-2 space-y-2">
              <Controller
                name="terms"
                control={control}
                render={({ field }) => (
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="terms"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500
                      dark:border-gray-600 dark:bg-gray-800 dark:checked:bg-blue-600 dark:checked:border-blue-600"
                    />
                    <Label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300 leading-snug">
                      {locale === "en" ? (
                        <>
                          I agree to the{" "}
                          <a
                            href="/legal/terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 font-medium hover:underline dark:text-blue-400"
                          >
                            Terms of Service
                          </a>{" "}
                          and{" "}
                          <a
                            href="/legal/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 font-medium hover:underline dark:text-blue-400"
                          >
                            Privacy Policy
                          </a>
                        </>
                      ) : (
                        <>
                          <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium hover:underline dark:text-blue-400">
                            የአገልግሎት ውሎች
                          </a> እና
                          <a
                            href="/legal/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 font-medium hover:underline dark:text-blue-400"
                          > የግላዊነት ፖሊሲ
                          </a> ተቀብያለሁ
                        </>
                      )}
                    </Label>
                  </div>
                )}
              />
              <p className="min-h-[18px] text-sm text-red-500 dark:text-red-400">{errors.terms?.message || ""}</p>
            </div>
          )}

          <SubmitButton
            text={buttonText}
            loadingText={locale === "en" ? "Loading" : "በማስቀመጥ ላይ..."}
            className="w-full px-4 py-3 rounded-lg font-bold text-white transition-colors duration-150
                      bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      dark:bg-blue-700 dark:hover:bg-blue-800 dark:focus:ring-blue-600 dark:focus:ring-offset-gray-900"
            disabled={submitting || isSubmitting || (mode === "signup" && !acceptedTerms)}
            loading={submitting || isSubmitting}
          />
        </form>

        {mode === "login" && (
          <p className="my-4 text-center text-sm text-gray-600 dark:text-gray-400">
            <a
              href="/auth/forgot-password"
              className="text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-500 dark:hover:text-blue-400"
            >
              {locale === "en" ? "Forgot Password?" : "የይለፍ ቃል ረስተዋል"}
            </a>
          </p>
        )}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          {secondaryLinkLabel}{" "}
          <a
            href={secondaryLinkHref}
            className="text-blue-600 hover:text-blue-800 font-medium hover:underline dark:text-blue-500 dark:hover:text-blue-400"
          >
            {secondaryLinkText}
          </a>
        </p>
      </div>
    </div>
  )
}

export default AuthForm